// externals
import React from 'react';

// components
import type { BadgeVariant } from '@interstate/components/Badge';
import { Badge } from '@interstate/components/Badge';
import { Flag } from '@interstate/components/Flag';
import { Box } from '@interstate/components/Box';
import { ChevronRightIcon } from '@interstate/components/Icons';
import IconRefresh from '@cx/ui/Icons/IconRefresh';
import { IconButton } from '@interstate/components/IconButton';
import { AiLogoIcon } from '@makemydeal/dr-shared-components';

// styles
import { AICardContainer, AICardLink, AICardActions, RefreshIcon } from './AiCard.style';

type AICardProps = {
    label: string;
    dataTestId?: string;
    message: string;
    status?: BadgeVariant;
    statusText?: string;
    onHandleLabelClick?: () => void;
    onRefresh?: () => void;
    canRefresh?: boolean;
    disabled?: boolean;
    showLogo?: boolean;
    creditScoreSource?: string;
};

const prefix = 'ai-card';

export const AICard = React.forwardRef<HTMLDivElement, AICardProps>(
    (
        {
            label,
            dataTestId,
            message,
            status,
            statusText,
            onHandleLabelClick,
            onRefresh,
            canRefresh = false,
            disabled = false,
            showLogo = true,
            creditScoreSource
        },
        ref
    ) => {
        const testIdPrefix = dataTestId ? `${prefix}-${dataTestId}` : prefix;

        const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            if (!disabled) {
                onHandleLabelClick?.();
            }
        };

        const handleRefresh = (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();
            onRefresh!();
        };

        const displayRefresh = !!(onRefresh && canRefresh);

        return (
            <AICardContainer ref={ref} data-testid={`${testIdPrefix}-container`}>
                <Box className={`${prefix}-header`}>
                    <Box className={`${prefix}-label-container`} data-testid={`${testIdPrefix}-label-container`}>
                        {showLogo && <AiLogoIcon data-testid={`${testIdPrefix}-logo`} fontSize="24px" ariaHidden={true} />}
                        <AICardLink href="#" onClick={handleClick} aria-disabled={disabled} disabled={disabled}>
                            <Box className={`${prefix}-label`} data-testid={`${testIdPrefix}-label`}>
                                {label}
                                <ChevronRightIcon />
                            </Box>
                        </AICardLink>
                    </Box>
                    <AICardActions>
                        {statusText && !disabled && (
                            <Badge data-testid={`${testIdPrefix}-badge`} variant={status}>
                                {statusText}
                            </Badge>
                        )}
                        {displayRefresh && (
                            <IconButton
                                data-testid={`${testIdPrefix}-refresh-button`}
                                onClick={handleRefresh}
                                size="sm"
                                aria-label="Refresh"
                                buttonStyle="tertiary"
                            >
                                <RefreshIcon>
                                    <IconRefresh />
                                </RefreshIcon>
                            </IconButton>
                        )}
                    </AICardActions>
                </Box>
                {message.length > 0 && (
                    <Box className={`${prefix}-message`} data-testid={`${testIdPrefix}-message`}>
                        {message}
                    </Box>
                )}
                {creditScoreSource && (
                    <Box className={`${prefix}-credit-score-source`} data-testid={`${testIdPrefix}-credit-score-source`}>
                        <Flag
                            variant="light"
                            data-testid={`${testIdPrefix}-credit-score-source-flag`}
                            sx={{ '.interstate-flag-head': { fontSize: '14px' } }}
                        >
                            {creditScoreSource}
                        </Flag>
                    </Box>
                )}
            </AICardContainer>
        );
    }
);

AICard.displayName = 'AICard';

export default AICard;
